import memoizeOne from 'memoize-one';
import { ff } from '@atlassian/jira-feature-flagging';
import { FIX_VERSIONS_TYPE } from '@atlassian/jira-platform-field-config';
import { cascadingSelectSupportedTypes } from '../field-types/cascading-select/supported-field-types';
import { devSummarySupportedTypes, DEV_SUMMARY_FIELD_KEY } from '../field-types/dev-summary';
import { goalsSupportedTypes } from '../field-types/goals';
import { groupSupportedTypes } from '../field-types/group/supported-field-types';
import { parentSupportedTypes } from '../field-types/parent';
import { projectSupportedTypes } from '../field-types/project/supported-field-types';
import { radioSelectSupportedTypes } from '../field-types/radio-select/supported-field-types';
import { resolutionSupportedTypes } from '../field-types/resolution/supported-field-types';
import { statusCategorySupportedTypes } from '../field-types/status-category/supported-field-types';
import { teamSupportedTypes } from '../field-types/team';
import { versionSupportedTypes } from '../field-types/version/supported-field-types';
import type { SUPPORTED_FIELD_TYPES, TOP_LEVEL_FIELD_TYPES } from './types';

export type { SUPPORTED_FIELD_TYPES, TOP_LEVEL_FIELD_TYPES };

// Used in useConstant to automatic add the supported field types
export const getSupportedFieldTypes = memoizeOne(
	(isSoftwareFieldsEnabled: boolean): SUPPORTED_FIELD_TYPES[] => [
		...parentSupportedTypes,
		...teamSupportedTypes,
		...(ff('jira.list.field-support-part1')
			? [
					...resolutionSupportedTypes,
					...groupSupportedTypes,
					...statusCategorySupportedTypes,
					...projectSupportedTypes,
					...cascadingSelectSupportedTypes,
					...radioSelectSupportedTypes,
				]
			: []),
		...(ff('native-goals-integration-in-list-view_h12va') ? goalsSupportedTypes : []),
		...(isSoftwareFieldsEnabled ? [...devSummarySupportedTypes, ...versionSupportedTypes] : []),
	],
);

/*
    newInitVisibleFields is just for the onboarding of the new fields
    should be removed when we do the clean up of: jsw_list_view_-_all_the_fields
*/

export const newInitVisibleFields = memoizeOne(
	(isSoftwareFieldsEnabled: boolean): SUPPORTED_FIELD_TYPES[] => [
		...parentSupportedTypes,
		...teamSupportedTypes,
		...(isSoftwareFieldsEnabled ? [FIX_VERSIONS_TYPE, ...devSummarySupportedTypes] : []),
	],
);

export const isSupportedTypePredicated = (
	fieldType: string,
	isSoftwareFieldsEnabled = true,
): fieldType is SUPPORTED_FIELD_TYPES =>
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	getSupportedFieldTypes(isSoftwareFieldsEnabled).includes(fieldType as SUPPORTED_FIELD_TYPES);

// top level field
const TOP_LEVEL_FIELD = [...devSummarySupportedTypes];

const isValidTopFieldType = (typename: unknown): typename is TOP_LEVEL_FIELD_TYPES => true;

export const getTopLevelFieldKey = (fieldType?: string) => {
	if (!fieldType || !isValidTopFieldType(fieldType)) {
		return null;
	}

	if (devSummarySupportedTypes.includes(fieldType)) {
		return DEV_SUMMARY_FIELD_KEY;
	}
	return null;
};

export const isTopLevelField = (fieldType?: string) => {
	if (!fieldType || !isValidTopFieldType(fieldType)) {
		return false;
	}
	return TOP_LEVEL_FIELD.includes(fieldType);
};
